/**
 * @fileOverview Scoreboard (/cricket/scores) page parser functions
 */

import selectn from 'selectn'
import { common } from '../../common'
import moment from 'moment-timezone'
import qs from 'query-string'
import helpers from '../gamepackage/helpers'
import { getYearDropDown, isCurrentLeague, isGeoRestricted, getTrackingImg } from '../../../../utils/common'
import dateFormat       from '../../../../utils/dateformat'
import eventSummary 	  from '../../rugby/eventSummary'
import translate        from '../../../../utils/translate'
import { translateURI }      	from '../../../../utils/translateURI'

export const scoreboard = {

  setLocalTime: function (leagues = [], scoreboardType = '', sport = '') {

    leagues.map((league) => {
      const events = league.events || []
      events.map((event) => {
        let timeValid = typeof event.timeValid !== 'undefined' ? event.timeValid : true
        let tzOptions = {
          timeZone : moment.tz.guess(),
          timeFormat: (sport == 'cricket' && !dateFormat.isTheGameToday(event.date)) ? 'ddd D/M - hh:mm a z' : 'hh:mm a z'
        }
        if(event.gameState === 'pregame') {
          const displayTime =  timeValid ? dateFormat.convertDateTime(event.date, tzOptions) : ''
          event.statusText = displayTime && displayTime.toUpperCase() || ''
        }

      })
    })
  },

  processTopEventScores: function ( res={}, edition ) {
    let topEventLeagues = []
    let defaultLeagues = []
    let allSports = selectn('data.sports', res) || []
    let topGameEvents = []

    if (!res.hasOwnProperty('data')) {
      return defaultLeagues
    }

    for (var i = 0; i < allSports.length; i++) {
      let data = allSports[i] || {}
      const sportType = data.name ? data.name.toLowerCase() : ''
      if(sportType == 'cricket') {
        let { leagues=[] } = data
        for(let j=0; j<leagues.length; j++) {
          let league = leagues[j]
          let events = scoreboard.parseTopEvents( league.events, edition, sportType ) || []
          topGameEvents.push(...events)
        }
      }
    }
    topEventLeagues.push({
      name: 'Top Events',
      text: 'Top Events',
      abbreviation: 'Top Events',
      id: '',
      events : topGameEvents,
      seriesHome: '',
      isTopEvents: true
    })

    return {
      topEventLeagues
    }
  },

  parseTopEvents: function ( events = [], edition, sport = 'cricket' ) {
    if (events.length === 0) {
      return events
    }

    let parsedEvents = events.map((ev = {}) => {
      const date = moment(ev.date || '').format('MMMM Do, YYYY')
      const state = selectn('fullStatus.type.state', ev)
      const fullStatus = ev.fullStatus || {}
      const competitors = ev.competitors || []
      const links = ev.links || []

      const gameState = common.getGameState(state)
      const teams = scoreboard.getTopEventTeams(competitors, gameState, fullStatus)
      const eventSummaryData = eventSummary.getSummaryFromDetails( sport, edition, competitors, [] )
      const eventClock = selectn('fullStatus.displayClock', ev) || '0\''

      let session = selectn('fullStatus.session', ev) || ''
      if(session) {
        session = session + ': '
      }
      let gameNote = session + selectn('fullStatus.summary', ev) || ''
      let timeValid = ev.timeValid || false
      const parsedEvent = {
        gameId: ev.id || '',
        gameDate: date,
        date: ev.date,
        teams: teams,
        gameState: gameState,
        statusText: scoreboard.getTopEventStatus( state, ev, edition.timeZoneBucket, sport ),
        winnerClass: common.getWinnerClass(competitors, sport),
        links: common.getGameLinks(links, sport, edition, false, gameState),
        gameUrl: common.getUrlByRel({ links }, 'match', edition.domain, sport),
        note: gameNote,
        info : ev.description || '',
        favInfo : ev.description || '',
        eventSummary : eventSummaryData,
        eventClock,
        timeValid,
        odds : ev.odds || {} // odds is a single objects in headerscoreboard api
      }
      return parsedEvent
    })
    return parsedEvents
  },

  /**
   * Get home and away teams details
   * @param  {Array}  competitors competitors array from feed
   * @param  {String} gameState pregame/live/final
   * @param {Object} fullStatus full status of the event
   * @return {Object}             teams object with {home, away}
   */
  getTopEventTeams: function (competitors=[], gameState, fullStatus) {
    let teams = {}
    let teamsOrder = []
    let isTeamBattingNow = false
    if(competitors.length > 1 && (selectn('[0].homeAway', competitors) === selectn('[1].homeAway', competitors))) {
      competitors[0].homeAway = 'home'
      competitors[1].homeAway = 'away'
    }
    competitors.map((competitor) => {
      let teamScore = scoreboard.getTeamScores(competitor)
      let [ runDetails, overDetails ] = common.splitScore(teamScore)
      if( gameState === 'live' && fullStatus.battingTeamId === competitor.id ) {
        isTeamBattingNow = true
      }

      let scoreMarkup = common.getFullScoreMarkup(teamScore, 'sm', 'div', true, isTeamBattingNow, false)
      let scoreMarkupSpan = common.getFullScoreMarkup(teamScore, 'cscore_overs', 'span', true, isTeamBattingNow, true)

      teams[competitor.homeAway] = {
        name: competitor.displayName,
        id: competitor.id,
        abbr: competitor.abbreviation,
        logo: common.getTeamLogo(competitor),
        href: '#',
        score: teamScore,
        runDetails: runDetails,
        overDetails: overDetails,
        scoreMarkup: scoreMarkup,
        scoreMarkupSpan: scoreMarkupSpan,
        links: '#',
        winner: competitor.winner || false,
        record: 'Form: ' + competitor.form || '-',
        order: competitor.order
      }
    })
    let homeTeamOrder = selectn('home.order',teams) || 0
    let awayTeamOrder = selectn('away.order', teams) || 0
    let gameInfo = ((teams.home && teams.home.name) + '_vs_' + (teams.away && teams.away.name))
    if(homeTeamOrder > 0 && awayTeamOrder > 0) {
      teamsOrder = homeTeamOrder == 1 ? [ 'home','away' ] : [ 'away', 'home' ]
    }
    else {
      Object.keys(teams).map((key)=> {
        teamsOrder.push(key)
      })
    }
    return { ...teams, teamsOrder, gameInfo }
  },

  /**
   * Wrapper function for passing cricket specific options to getGameStatusText
   * @param  {String} state  game state
   * @param  {Object} events events
   * @param  {String} timeZoneBucket  timeZoneBucket
   * @param  {String} sport  sport
   * @return {String}        Output of getGameStatusText
   */
  getTopEventStatus: function ( state, events, timeZoneBucket, sport ) {
    let statusText = ''
    let tzOptions = {
      // Set time zone to IST
      // TODO: use time zones based on editions
      timeZone : process.browser && moment.tz.guess() || timeZoneBucket,
      //timeFormat : 'hh:mm a z',
      timeFormat: (sport === 'cricket' && !dateFormat.isTheGameToday(events.date)) ? 'ddd D/M - hh:mm a z' : 'hh:mm a z'
    }
    switch (state) {
      case 'pre':
        statusText = dateFormat.convertDateTime(events.date, tzOptions)
        break

      case 'in':
      case 'post':
        statusText = selectn('fullStatus.type.description', events) || selectn('fullStatus.type.detail', events) || selectn('fullStatus.type.shortDetail', events) || state
        break
    }
    statusText = statusText.toUpperCase()
    return statusText
  },

  /**
   * Process data received from the APIs
   * @param  {Object} res       results from API
   * @param  {Object} edition   edition config
   * @param  {Object} params    params
   * @param  {Object} location  Router location
   * @param  {Object} appProps  app Props
   * @param  {Array}  topEventIds topevents list
   * @return {Object}           leagues and date
   */

  processData: function ( res, edition, params, location, appProps, topEventIds=[] ) {
    // Default return types
    const query = location.query || qs.parse(location.search)
    const device = appProps.device || {}
    const adblock = selectn('_adblock', query) === 'true' ? true : false
    const siteType = appProps.siteType
    const isDataLite = (siteType === 'data-lite') ? true : false
    let defaultLeagues = []
    let defaultDate = new Date()
    let meta = {}
    let teamName = ''
    let seriesName = ''
    let siteNameMap = {
      'www.espncricinfo.com': 'ESPNCricinfo',
      'www.espncricinfosb.com': 'ESPNCricinfo'
    }
    let siteName = siteNameMap[edition.domain] || 'ESPN'
    let title = ''
    let FixturesResultsText = ''
    let heading = ''
    let defaultActiveDates = []
    let showWeek = false
    let multipleLeagues = true
    let gamesPresent = false
    let seriesId = ''
    let scoreboardType = 'default'
    let currYear = new Date().getFullYear()
    let firstSeries = ''
    // consutruct year array from current calender year to last 6 year
    let dateFilter = getYearDropDown()
    let desktopScoreboard = (edition.isEspnCricinfo && device && device.desktop && scoreboard.getDesktopScoreboard()) || {}
    const domain = edition.domain || ''
    let standings = []
    let filters = {}
    let downloadFixtureUrl = ''
    // if year is passed as query then current year will fetch from query string else current calender year
    const queryDate = query.date
    if(typeof queryDate !== 'undefined' && queryDate !== '') {
      currYear = moment(queryDate, 'YYYYMMDD').format('YYYY') || queryDate
    }

    // Extract sport from params
    let sport
    if (params && Object.keys(params).length > 0) {
      sport = 'sport' in params ? params.sport : 'cricket'
    }

    // Default scoreboard data
    let defaultData = {
      leagues : defaultLeagues,
      date : defaultDate,
      title: translate( edition, ('dropdown.leagues.default.' + sport) ) || '',
      heading: heading,
      activeDates: defaultActiveDates,
      showWeek:showWeek
    }

    // Return default data if res evaluates to a falsy value
    if (!res || res.hasOwnProperty('data') === false) {
      return defaultData
    }

    const ucSport = sport.charAt(0).toUpperCase() + sport.slice(1)
    const ucDomain = domain.replace('www.','').replace('espn','ESPN')
    let page = {
      ucSport,
      ucDomain,
      currYear,
      seriesName: '',
      teamName: '',
      heading: '',
      title: 'Live '+ucSport+' Scores & Fixtures | ' + siteName,
      description: 'Get live '+ucSport+' scores and performance stats for tours, leagues, and championship matches on ESPN',
      keywords: 'live '+sport+' score, latest, update, ball by ball, match scores'
    }


    // Return default data if scores are undefined or not an array
    let allScores
    let newSlug = ''
    if(selectn('data.scores', res)) {

      allScores = selectn('data.scores', res)

      if(typeof params.team !== 'undefined') {
        teamName = selectn('data.team.displayName', res) || ''
        if(sport == 'cricket') {
          title = heading = selectn('data.scores[0].displayName', res) || teamName || ''
          title = heading = `${heading}${heading.length>0 ? ' - ' : ''}Fixtures & Results`
          multipleLeagues = false
        } else {
          title = heading = selectn('data.scores[0].leagues[0].name', res) || ''
          multipleLeagues = false
        }
        scoreboardType = 'team'
        page.teamName = (teamName !== '')?  teamName : ''

        if(typeof query.series !== 'undefined') {
          seriesId = query.series
        } else {
          firstSeries = selectn('data.scores[0].leagues[0].id', res) || ''
          if(firstSeries) {
            seriesId = scoreboard.getCurrentLeague(allScores, firstSeries) || ''
          }
        }
      } else {
        multipleLeagues = true

        // use translation tool to get correct key
        let defaultKey = 'dropdown.leagues.default.' + sport
        title = translate( edition, defaultKey )
        if ( title === defaultKey ) {
          title = translate( edition, 'dropdown.leagues.default.sport' )
        }

        //title = 'All Series'
        heading = 'Live Cricket Scores'
        if( sport === 'rugby' ) {
          heading = translate(edition, 'Rugby Scores')
          //title = translate(edition, 'dropdown.leagues.default.rugby')
        } else if ( sport === 'nrl' ) {
          heading = 'NRL Scores'
          //title = translate(edition, 'dropdown.leagues.default.rugby')
        }

      }
    } else if(selectn('data.leagues', res)) {
      scoreboardType = 'series'
      multipleLeagues = false
      allScores = [ res.data ]
      seriesName = title = heading = selectn('data.leagues[0].name', res) || ''
      FixturesResultsText = translate(edition, 'Fixtures and Results')
      title = heading = `${heading}${heading.length>0 ? ' - ' : ''}` + FixturesResultsText
      page.seriesName =  seriesName !== ''? seriesName : ''
      standings = selectn('data.standings', res) || []
      newSlug = selectn('data.leagues[0].slug', res) || ''
    }
    if ( typeof allScores === 'undefined' || Array.isArray(allScores) === false ) {
      return defaultData
    }

    let team = res.data && res.data.team || {}

    if (team && params.team) {
      newSlug = (team && (team.slug || team.displayName)) || ''
    } else if (newSlug && params.league) {
      newSlug = selectn('data.leagues[0].slug', res) || ''
    }
    page.slug = common.getSafeSlug(newSlug)
    // Process date of returned games from first game's date
    let apiDate = selectn('data.scores[0].events[0].date', res) || selectn('data.events[0].date', res)
    // use default value if date was not found
    let date = typeof apiDate !== 'undefined' ? apiDate : defaultDate

    date = scoreboard.getDate(date, params, query)

    const polling = scoreboard.shouldPoll(date, params)

    // By default, set fastcast to false
    let fastCastMeta = {
      enabled: false
    }

    /**
     * Fastcast should be enabled only for scores page
     *
     * Fastcast should not be enabled for team & series bases scores pages
     * since we do not have dedicated for those.
     *
     * Also, it should not be enabled for any date other than today's date
     */
    if (params.hasOwnProperty('sport') && params.sport === 'cricket') {
      // Do not enable for team & series scores pages
      if (params.hasOwnProperty('team') === false && params.hasOwnProperty('league') === false) {
        fastCastMeta = {
          enabled: polling.enabled,
          scoresTopic : 'scoreboard-cricket',
          topEventTopic: edition.headerSBTopicId ? `event-topevents-${edition.headerSBTopicId}` : ''
        }
        // Check if date is passed via query string
        if (query && query.hasOwnProperty('date')) {
          const curDate = moment.utc().format('YYYYMMDD')
          // If queried date is different than current date, disable fastcast
          if (query.date !== curDate) {
            fastCastMeta.enabled = false
            fastCastMeta.scoresTopic = undefined
            fastCastMeta.topEventTopic = undefined
          }
        }
      }
    }

    //console.info('scoreboard parser', fastCastMeta)

    // Iterate over the scores and extract league information
    if(fastCastMeta.enabled) {
      polling.duration = 15000
    }
    let leagues = []
    let isToday = false
    let topEvents = []
    if(sport === 'cricket' && scoreboardType === 'default') {
      isToday = !query.date || (dateFormat.dateToObject(query.date || '').setHours(0,0,0,0) == defaultDate.setHours(0,0,0,0))
      if( isToday ) {
        topEvents = topEventIds.length ? topEventIds : scoreboard.getTopEvents(params.sport, allScores)
      }
    }
    if (sport === 'cricket' && scoreboardType === 'series') {
      const leagueClasses = selectn('data.leagues[0].classId', res) || []
      const leagueTeams = selectn('data.teams', res) || []
      filters = scoreboard.getFilters(leagueClasses, leagueTeams, query, location)
      const legacyLeagueId = selectn('data.leagues[0].mappings.cricinfo', res) || ''
      const currDate = moment(new Date()).format() || ''
      const endDate = moment(selectn('data.leagues[0].calendarEndDate', res)).format() || ''
      downloadFixtureUrl = currDate < endDate ? scoreboard.getDownloadFixtureUrl(legacyLeagueId) : ''
    }

    for (var i = 0; i < allScores.length; i++) {
      const data = allScores[i]
      /**
       * data object has 2 properties.
       * extract league information from leagues.
       * extract matches information from events.
       */
      const league = selectn('leagues[0]', data)
      if (typeof league !== 'undefined' && Object.keys(league).length > 0) {
        // set default matches for this league
        let matches = []
        let classId
        let geoRank
        let classIdGroup
        if(scoreboardType === 'team' && sport !== 'cricket') {
          if(seriesId === league.id) {
            ({ matches=[] } = scoreboard.parseEvents( params.sport, data.events, edition, topEvents, filters, scoreboardType, query ) || {})
            title = heading = league.name || ''
          }
        } else {
          ({ matches=[], classId='', geoRank=0, classIdGroup=5 } = scoreboard.parseEvents( params.sport, data.events, edition, topEvents, filters, scoreboardType, query ) || {})
        }
        if (!gamesPresent) {
          gamesPresent = matches.length > 0 ? true : false
        }
        let name = league.name
        if(scoreboardType === 'team' && league.abbreviation) {
          name = league.abbreviation
        }
        let leagueLinks = {
          links: [ league.links || {} ]
        }
        if(matches.length) {
          leagues.push({
            name: name,
            text: name,
            abbreviation: league.abbreviation,
            calendar: league.calendar,
            slug: common.getSafeSlug(league.slug || ''),
            id: league.id,
            events : matches,
            sport: sport,
            seriesHome: common.extractUrl(leagueLinks, 'Series Home', 'desktop', edition.domain, sport),
            classId,
            geoRank,
            classIdGroup
          })
        }
      }
    }

    if(!allScores.length) {
      gamesPresent = false
    }
    let sortedLeagues = []
    if(sport == 'cricket') {
      sortedLeagues = leagues.sort((a, b)=>( (a.classIdGroup - b.classIdGroup) || (b.geoRank - a.geoRank) || (a.classId - b.classId)))
    } else {
      sortedLeagues = leagues
    }
    let noGamesText = ''
    if (!gamesPresent) {
      noGamesText = translate(edition, 'No_Games_On_This_Date') || ''
    }

    const viewTodayText = translate(edition, 'ViewToday') || ''
    const scoresForText = translate(edition, 'Scores for') || ''
    const outbrainHeadline = translate(edition, 'sponsoredHeadlines') || ''

    const activeDates = scoreboard.getActiveDates(leagues)
    //console.info('processData output', { leagues, date, activeDates, title })
    page.heading = heading
    meta = scoreboard.getMetaData(edition, params, page)

    const analytics = scoreboard.getAnalytics(meta, page, edition)
    const trackingImg = getTrackingImg(edition, params.pageType, page.seriesName, page.slug, '', location.pathname, siteType, '', query, '', scoreboardType) // analytics

    return {
      standings,
      noGamesText,
      viewTodayText,
      scoresForText,
      outbrainHeadline,
      device,
      meta,
      analytics,
      leagues: sortedLeagues,
      date,
      apiDate,
      ISOdate : date.format(),
      activeDates,
      title,
      polling,
      fastCastMeta,
      heading,
      showWeek,
      multipleLeagues,
      scoreboardType,
      currYear,
      dateFilter,
      desktopScoreboard,
      topEvents,
      domain,
      isToday,
      adblock,
      isDataLite,
      trackingImg,
      filters,
      downloadFixtureUrl
    }
  },

  getTopEvents(sport = 'cricket', allScores = []) {
    let topEvents = []
    if(sport === 'cricket') {
      allScores.map(data => {
        if(data.events) {
          data.events.map(event => {
            if(event.isTopEvent) {
              topEvents.push(event.id)
            }
          })
        }
      })
    }

    return topEvents
  },

  getAnalytics: function (meta) {
    let metrics = {}
    return {
      chartbeat: {
        title: meta.title
      },
      metrics
    }
  },

  getMetaData: function ( edition={}, params={}, page={} ) {
    // TODO : check for 'All Series' and restrict from inserting
    let leagueTitle = page.heading
    let league = params.league || ''
    let sport = params && (params.sport || '')
    let ucSport = page.ucSport || sport
    let transKeyObj = {}
    if( params.sport === 'cricket' ) {
      if(!page.teamName && !page.seriesName) {
        transKeyObj = {
          title : {
            key : 'meta.title.cricket.scoreboard',
            domain : page.ucDomain
          },
          description : {
            key : 'meta.description.cricket.scoreboard',
            domain : page.ucDomain,
            year : page.currYear
          },
          keywords : {
            key : 'meta.keywords.cricket.scoreboard'
          }
        }
      }
      if(page.teamName !== '') {
        transKeyObj = {
          title : {
            key : 'meta.title.cricket.scoreboard.team',
            team : page.teamName,
            year : page.currYear,
            domain :  page.ucDomain
          },
          description : {
            key : 'meta.description.cricket.scoreboard.team',
            team : page.teamName,
            year : page.currYear,
            domain :  page.ucDomain
          },
          keywords : {
            key : 'meta.keywords.cricket.scoreboard.team',
            team : page.teamName
          }
        }
      }
      if(page.seriesName !== '') {
        transKeyObj = {
          title : {
            key : 'meta.title.cricket.scoreboard.series',
            series : page.seriesName,
            domain :  page.ucDomain
          },
          description : {
            key : 'meta.description.cricket.scoreboard.series',
            series : page.seriesName,
            domain :  page.ucDomain
          },
          keywords : {
            key : 'meta.keywords.cricket.scoreboard.series',
            series : page.seriesName
          }
        }
      }
    }

    if( sport === 'rugby' ) {
      transKeyObj = {
        title : {
          key : 'meta.title.rugby.scoreboard',
          leagueHeading : leagueTitle
        },
        keywords: {
          key : 'meta.keywords.rugby.scoreboard',
          leagueHeading : leagueTitle
        },
        description: {
          key : 'meta.description.rugby.scoreboard',
          leagueHeading : leagueTitle
        }
      }
    }

    let hrefConfig = { showSport: true }
    if(edition.site === 'espncricinfo') {
      hrefConfig.showSport = false
    }
    let canonical = common.createCanonical(edition, params, hrefConfig, page.slug)
    return {
      title: translate(edition, transKeyObj.title) || page.title,
      description: translate(edition, transKeyObj.description) || page.description,
      keywords: translate(edition, transKeyObj.keywords) || page.keywords,
      canonical: canonical,
      website: 'article',
      sport: ucSport && ucSport.toLowerCase()
    }
  },

  /**
  * getDesktopScoreboard builds desktop scoreboard info for RHS
  * @param  {Object} device   deviceInfo
  * @return {Object} Object {title , textDescription, href, linkText}
  **/
  getDesktopScoreboard: function () {
    let title = 'Desktop Scoreboard' || ''
    let textDescription = 'A mini scoreboard that stays with you and updates all the current matches' || ''
    let href = '/ci/engine/match/scores/desktop.html' || '#'
    let linkText = 'Launch Now' || ''
    return {
      title : title,
      textDescription : textDescription,
      link : {
        href : href,
        text : linkText
      }
    }
  },

  /**
   * Check if videos information present
   * @param  {Array}  highlights details
   * @param  {String}  country details
   * @return {Boolean} false if empty else true
   */
  isHighlightPresent: function (highlights = [], country='') {
    let highlightVideo = highlights
    if(highlightVideo.length > 0) {
      highlightVideo = highlightVideo.filter(v => {
        return !isGeoRestricted(v.geoRestrictions, country.toUpperCase())
      })
    }
    return highlightVideo.length > 0 ? true : false
  },

  /**
   * Parse events/games for cricket
   * @param  {String} sport     The current sport
   * @param  {Array}  events    list of sports matches
   * @param  {Object} edition   edition
   * @param  {Array}  topEvents topEvent Ids to filter
   * @param  {Object} filters filters for series filter (team/match type)
   * @param  {String} scoreboardType   scoreboardType
   * @param  {String} query     URL Query objects
   * @return {Array}            program friendly parsed list of matches.Each Match
   *                            object will have keys {details, gameState, links,
   *                            statusText, teams, weather, winnerClass}
   */
  parseEvents: function ( sport = '', events = [], edition, topEvents=[], filters = {}, scoreboardType = 'default', query ) {
    // Return empty array if there are no events
    if (events.length === 0) {
      return events
    }

    // Iterate over each event/game and extract information
    let classIds = []
    let geos = []
    let filterType = filters.type
    let filterValue = filters.value
    let exclClassIds = [ 1, 2, 3 ]
    let lang = edition.language || 'en'
    // grouping classIds as International, domestic, women, youth etc
    let classIdGroups = {
      1: 1,
      2: 1,
      3: 1,
      4: 4,
      5: 4,
      6: 2,
      7: 4,
      8: 3,
      9: 3,
      10: 3,
      11: 5,
      12: 5,
      13: 5,
      14: 5,
      15: 5,
      16: 5
    }
    let parsedEvents = [], queryDate = query.date
    for(let i=0 ; i<events.length ; i++) {
      const ev = events[i]
      const gameId = ev['id']
      if(topEvents.indexOf(gameId)>=0) {
        continue
      }
      const date = moment(ev['date']).format('MMMM Do, YYYY')
      const competition = selectn('competitions[0]', ev) || {}

      const filterClassId = parseInt(selectn('class.internationalClassId', competition)) || parseInt(selectn('class.generalClassId', competition))
      classIds.push(filterClassId)

      if(filterValue && filterType === 'class') {
        if(filterValue === 'others' ) {
          if(exclClassIds.indexOf(filterClassId) !== -1) { // skiping test, odi n t20i class ids
            continue
          }
        }else if(filterValue !== parseInt(filterClassId)) {
          continue
        }
      }

      const state = selectn('status.type.state', ev)
      const competitors = competition.competitors || []
      const odds = selectn('[0]', competition.odds) || {} // odds are array of objects in scorepanel api
      const details = competition.details || []
      //const status = ev.status || {}
      let links = selectn('links', ev) || []

      //do not show articles for es and pt
      if(lang === 'es' || lang === 'pt') {
        let esLinks = []
        const linksLen = links.length || 0
        for(let i=0; i < linksLen; i++) {
          const item = links[i] || {}
          const rel = item.rel || []
          if(!((rel.indexOf('recap') > -1) || (rel.indexOf('match') > -1))) {
            esLinks.push(item)
          }
        }
        links = esLinks
      }

      const highlights = competition.highlights || []
      const timeValid = competition.timeValid || false
      const gameState = common.getGameState(state)
      const teams = scoreboard.getTeams(competitors, gameState)

      let homeTeamId = selectn('home.id',teams) || 0
      let awayTeamId = selectn('away.id', teams) || 0

      if(filterType == 'team' && filterValue && !( filterValue === homeTeamId || filterValue === awayTeamId ) ) {
        continue
      }

      // for favourite module pick description rest sombination of shortdescription + date from description
      const matchDesc = ev['description']
      const eventDateInfo = (typeof matchDesc === 'undefined') ? '' : matchDesc.substring(matchDesc.lastIndexOf(','), matchDesc.length)
      const info = competition.shortDescription
      const isTopEvent = ev.isTopEvent || false

      const eventSummaryData = eventSummary.getSummaryFromDetails( sport, edition, competitors, details )
      const eventClock = selectn('status.displayClock', competition) || '0\''

      geos = geos.concat(teams.teamGeo || [])

      let gameUrl = common.getUrlByRel({ links }, 'match', edition.domain, sport, edition.isEspnCricinfo)

      // CTOI-35558
      // hack for generating gameUrl from code till API is fixed
      if(lang === 'es' || lang === 'pt') {
        gameUrl = common.getUrlByRel({ links }, 'summary', edition.domain, sport)
      }

      //Translate URLs
      let localizedHref = translateURI(edition, { url: gameUrl }, false)
      gameUrl = localizedHref.url

      const parsedEvent = {
        gameId: gameId,
        gameDate: date,
        date: ev['date'],
        teams: teams,
        gameState: gameState,
        statusText: scoreboard.getCricketGameStatus( sport, competition, edition.timeZoneBucket, queryDate,timeValid ),
        winnerClass: common.getWinnerClass(competitors, sport),
        links: common.getGameLinks(links, sport, edition, scoreboard.isHighlightPresent(highlights, edition.userGeo), gameState),
        gameUrl: gameUrl,
        note: scoreboard.getGameNote(ev),
        info : info + eventDateInfo,
        favInfo : matchDesc,
        eventSummary : eventSummaryData,
        eventClock,
        odds,
        isTopEvent,
        timeValid
      }

      parsedEvents.push(parsedEvent)
    }
    let { userGeo = '' } = edition
    let geoRank = (geos.indexOf(userGeo.toUpperCase()) >=0 ) ? 1 : 0
    let classId = Math.min(...classIds)
    let classIdGroup = classIdGroups[classId] || 0
    return { matches: parsedEvents, classId, geoRank, classIdGroup }
  },


  /**
   * Wrapper function for passing cricket specific options to getGameStatusText
   * @param  {[type]} sport           Sport
   * @param  {[type]} competition     competition object describing game
   * @param  {String} timeZoneBucket  timeZoneBucket
   * @param {Object} queryDate Date for which scoreboard is being displayed
   * @param {Boolean} timeValid timeValid
   * @return {[type]}                 Output of getGameStatusText
   */
  getCricketGameStatus: function ( sport, competition, timeZoneBucket, queryDate, timeValid=true ) {
    let competitionDate = competition.date, tzOptions = {
      // Set time zone to IST
      // TODO: use time zones based on editions
      timeZone : process.browser && moment.tz.guess() || timeZoneBucket,
      timeFormat: (sport === 'cricket' && !dateFormat.isTheGameToday(competitionDate, queryDate)) ? 'ddd D/M - hh:mm a z' : 'hh:mm a z'
    }
    //console.info('tzOptions', tzOptions)
    return common.getGameStatusText( sport, competition, tzOptions, timeValid ).toUpperCase()
  },


  /**
   * Get game note
   * @param  {Object} ev event/game object
   * @return {String}    game note if found else empty string
   */
  getGameNote: function (ev = {}) {
    let defaultNote = ''
    let session = selectn('status.session', ev) || ''
    if(session) {
      session = session + ': '
    }
    let gameNote = session + selectn('status.summary', ev)
    // Return game note if found else return default game note
    return typeof gameNote !== 'undefined' ? gameNote : defaultNote
  },


  /**
   * Get home and away teams details
   * @param  {Array}  competitors competitors array from feed
   * @param  {String} gameState pregame/live/final
   * @return {Object}             teams object with {home, away}
   */
  getTeams: function (competitors=[], gameState) {
    let teams = {}
    let teamsOrder = []
    let teamGeo = []
    if(competitors.length > 1 && (selectn('[0].homeAway', competitors) === selectn('[1].homeAway', competitors))) {
      competitors[0].homeAway = 'home'
      competitors[1].homeAway = 'away'
    }
    competitors.map((competitor) => {
      // default to empty object if not available
      const team = competitor.team || {}
      let teamScore = scoreboard.getTeamScores(competitor)
      // Get breakup of runs and overs/target
      let [ runDetails, overDetails ] = common.splitScore(teamScore)
      // Is this team batting now?
      let isTeamBattingNow = helpers.isTeamBattingNow(competitor, gameState)
      // If team is batting in current innings, suffix * as per cricket convention
      let scoreMarkup = common.getFullScoreMarkup(teamScore, 'sm', 'div', true, isTeamBattingNow, false)
      let scoreMarkupSpan = common.getFullScoreMarkup(teamScore, 'cscore_overs', 'span', true, isTeamBattingNow, true)
      // Populate teams object with home and away data
      teams[competitor.homeAway] = {
        name: team.name,
        id: team.id,
        abbr: team.abbreviation || team.shortDisplayName,
        logo: common.getTeamLogo(team),
        href: common.getTeamUrl(team),
        score: teamScore,
        runDetails: runDetails,
        overDetails: overDetails,
        scoreMarkup: scoreMarkup,
        scoreMarkupSpan: scoreMarkupSpan,
        links: scoreboard.getTeamLinks(team),
        winner: competitor.winner || false,
        record: 'Form: ' + competitor.form || '-',
        order: competitor.order,
        countryCode: team.countryCode
      }
    })

    let homeTeamGeo = (selectn('home.countryCode',teams) || '').toUpperCase()
    let awayTeamGeo = (selectn('away.countryCode',teams) || '').toUpperCase()

    let gameInfo = ((teams.home && teams.home.name) + '_vs_' + (teams.away && teams.away.name))
    teamGeo = [ homeTeamGeo, awayTeamGeo ]

    let homeTeamOrder = selectn('home.order',teams) || 0
    let awayTeamOrder = selectn('away.order', teams) || 0
    if(homeTeamOrder > 0 && awayTeamOrder > 0) {
      teamsOrder = homeTeamOrder == 1 ? [ 'home','away' ] : [ 'away', 'home' ]
    }
    else {
      Object.keys(teams).map((key)=> {
        teamsOrder.push(key)
      })
    }

    return { ...teams, teamsOrder, teamGeo, gameInfo }
  },

  getCurrentLeague: function (scores=[], prevSeriesId) {
    let startDate = ''
    let endDate = ''
    let calendar = []
    let currDate = moment.utc(new Date()).format()
    let currSeries = ''

    for (var i = 0; i < scores.length; i++) {
      let score = scores[i]
      calendar = selectn('leagues[0].calendar', score)
      if(calendar.length > 0) {
        startDate = calendar[0]
        startDate = moment(startDate).format()
        endDate = calendar[calendar.length - 1]
        endDate = moment(endDate).format()
        let iscurr = isCurrentLeague(startDate, endDate, currDate)
        //currSeries = selectn('leagues[0].id', score)
        //console.info('calendar:::::::::', currDate, startDate, endDate, iscurr, currSeries)
        //console.info('comparassion:::::', currDate > startDate, prevSeriesId)
        if(iscurr) {
          currSeries = selectn('leagues[0].id', score)
          break
        } else if(currDate > startDate) {
          currSeries = prevSeriesId
        } else if(currDate < startDate) {
          prevSeriesId = selectn('leagues[0].id', score)
        }
      }

    }
    //console.info('currSeries::::::::::', currSeries)
    return currSeries
  },


  /**
   * Get score of a team
   * @param  {Object} competitor Each competitior from competitors array
   * @return {String}            Score if found else empty string
   */
  getTeamScores: function (competitor = {}) {
    return 'score' in competitor ? competitor.score : ''
  },

  /**
   * Filter links for teams. Used as a callback function.
   * @param  {Object} obj Link Object
   * @return {Boolean}     true if link text is found in valid array else false
   */
  filterLinks: function (obj) {
    // Lookup links with text
    let valid = [ 'Fixtures', 'Statistics', 'Squad', 'Clubhouse' ]
    let linkText = obj.text
    // Return false If valid string is not found in the link text
    if (valid.indexOf(linkText) === -1) {
      return false
    }
    // Get link type object
    let linkType = obj.rel
    // valid link type
    let validType = 'desktop'
    // Return true if desktop is found in rel array of the link object
    if (Array.isArray(linkType) === true) {
      if (linkType.indexOf(validType) > -1) {
        return true
      }
    }
    return false
  },

  /**
   * Get team links for 'Fixtures', 'Statistics', 'Squad'
   * @param  {Object} team team Object
   * @return {Array}       Array of team links
   */
  getTeamLinks: function (team) {
    let teamLinks = []
    //console.info('inside getTeamLinks', team)
    let links = team.links
    // filter out required links
    teamLinks = links.filter(scoreboard.filterLinks)
    //console.info('links, size', links, links.length)
    //console.info('teamLinks, size', teamLinks, teamLinks.length)
    let href = selectn('[0].href',teamLinks) || ''
    if (href !== '') {
      teamLinks[0].href = common.buildURL(href)
    }

    return teamLinks
  },

  getActiveDates: function (leagues) {
    let dates = {}
    let calendar = []

    if(typeof leagues !== 'undefined') {
      leagues.map(function (league) {
        calendar = calendar.concat(league.calendar)
      })

      calendar.map(function (date) {
        dates[(new Date(date).setHours(0,0,0,0))] = 1
      })

      return dates
    }
  },

  getDate: function (date, params = {}, query = {}) {
    let dateQuery = query.date && moment(query.date, 'YYYYMMDD') || null
    let leagueQuery = params.league || query.league
    let eventDate = typeof date !== 'undefined' ? moment(date, 'YYYYMMDD') : moment.utc()
    // default date is the date passed as this is the one got from the first match in the results
    let finalDate = params.sport !== 'cricket' ? eventDate : moment.utc()

    // if league query, use event date
    if(leagueQuery) {
      finalDate = eventDate
    }

    // dateQuery supercedes all dates
    if (dateQuery) {
      finalDate = dateQuery
    }

    return finalDate
  },
  getDownloadFixtureUrl(id) {
    let legacyurl = ''

    if(id.length > 0) {
      legacyurl = 'https://www.espncricinfo.com/ci/content/series/' + id + '.ics?template=ical'
    }
    return legacyurl
  },
  shouldPoll(date, params={}) {
    const duration = 20000
    let enabled = false
    if(params.hasOwnProperty('team') || params.hasOwnProperty('league')) {
      return { enabled, duration }
    }
    const startTime = moment.utc()
    const endTime = moment(date)
    const diff = endTime.diff(startTime, 'days')
    // console.log(startTime.format(), endTime.format(), diff)
    // TODO: update and optimize logic
    // polling 5 days, before and after the game, considering test games
    if(Math.abs(diff) <= 5) {
      enabled = true
    }
    return { enabled, duration }
  },
  getFilters(classIds = [], teams = [], query = {}, location = {}) {
    let filters = {
      list: []
    }
    let classIdMapping = {
      1: 'Tests',
      2: 'ODI',
      3: 'T20i'
    }
    let pushedIds = []
    let pushedFilters = []
    let link = location.pathname
    if (classIds.length || teams.length) {
      filters.list.push({
        text: 'All',
        href: location.pathname
      })
    }
    if (classIds.length > 1) {
      filters.type = 'class'
      for (let i = 0; i < classIds.length; i++) {
        let displayText = classIdMapping[classIds[i]] || 'Others'
        if (pushedIds.indexOf(classIds[i]) === -1 && pushedFilters.indexOf(displayText) === -1) {
          pushedIds.push(classIds[i] || 'others')
          pushedFilters.push(displayText)
          filters.list.push({
            text: displayText,
            href: `${link}?type=${displayText.toLowerCase()}`
          })
        }
        if (query.type == displayText.toLowerCase()) {
          filters.displayValue = displayText
          filters.value = [ 1,2,3 ].indexOf(parseInt(classIds[i])) == -1 ? 'others' : parseInt(classIds[i])
        }
      }
    } else if (teams.length) {
      filters.type = 'team'
      for (let i = 0; i < teams.length; i++) {
        let ithTeam = teams[i] || {}
        let teamId = ithTeam.id || ''
        let teamName = ithTeam.displayName || ithTeam.name || ''
        if (query.teamid && query.teamid == teamId) {
          filters.value = teamId
          filters.displayValue = teamName
        }
        if (pushedIds.indexOf(teamId) == -1 && teamName && teamId) {
          pushedIds.push(teamId)
          filters.list.push({
            text: teamName,
            href: `${link}?teamid=${teamId}`
          })
        }
      }
    }
    return filters
  }

}

export default scoreboard



// WEBPACK FOOTER //
// ../app/lib/transform/parsers/cricket/scoreboard/index.js